import React from 'react';
import { Box, Typography } from '@mui/material';
import { Timeline } from '@mui/icons-material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ReferenceLine,
} from 'recharts';
import { colors, status } from '../../theme';

export default function ScanHistorySection({ t, scanDates, selectedDate, setSelectedDate }) {
  if (scanDates.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2.5, borderRadius: 1.5, border: `1px solid ${colors.gray[200]}`, bgcolor: colors.background.paper, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.2, borderBottom: `1px solid ${colors.gray[100]}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Timeline sx={{ fontSize: 14, color: status.purple.main }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: colors.gray[600] }}>
            {t('vpa.history.timeline', 'Scan history')}
          </Typography>
          {selectedDate && (
            <Typography
              onClick={() => setSelectedDate(null)}
              sx={{ fontSize: '0.62rem', fontWeight: 600, color: status.purple.text, cursor: 'pointer', ml: 1, '&:hover': { textDecoration: 'underline' } }}
            >
              {t('vpa.history.latest', '← latest')}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: '0.62rem', color: colors.gray[400], fontFamily: 'monospace' }}>
          {selectedDate || scanDates[0]?.date}
        </Typography>
      </Box>
      <Box sx={{ height: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={[...scanDates].reverse()}
            margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
            onClick={(event) => {
              const date = event?.activePayload?.[0]?.payload?.date;
              if (date) setSelectedDate(date === scanDates[0]?.date ? null : date);
            }}
          >
            <defs>
              <linearGradient id="vpaScanGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={status.purple.main} stopOpacity={0.3} />
                <stop offset="95%" stopColor={status.purple.main} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.gray[100]} />
            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(date) => (date || '').slice(5)} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 9 }} width={28} />
            <RTooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const point = payload[0].payload;
                return (
                  <Box sx={{ bgcolor: colors.background.paper, border: `1px solid ${colors.gray[200]}`, borderRadius: 1.5, px: 1.5, py: 1, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    <Typography sx={{ fontSize: '0.7rem', color: colors.gray[400], mb: 0.3, fontFamily: 'monospace' }}>{label}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: status.purple.text }}>
                      Avg score: {point.avg_score ?? '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: colors.success.main }}>READY: {point.ready_count}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: colors.info.main }}>WATCH: {point.watch_count}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: colors.warning.main }}>RADAR: {point.radar_count}</Typography>
                  </Box>
                );
              }}
            />
            <ReferenceLine y={50} stroke={colors.warning.main} strokeDasharray="4 4" strokeOpacity={0.5} />
            <Area type="monotone" dataKey="avg_score" stroke={status.purple.main} fill="url(#vpaScanGrad)" strokeWidth={2} />
            {selectedDate && <ReferenceLine x={selectedDate} stroke={status.purple.text} strokeWidth={2} />}
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
